import { useState, useMemo, useContext, useCallback, useEffect } from 'react'
import { TelemetryDataContext } from '../context/TelemetryDataContext'
import { SessionDataContext } from '../context/SessionDataContext'
import { StudentAnalyticsContext } from '../context/StudentAnalyticsContext'
import { batchAssessConversation } from '../../../service/gptService'
import { getMessagesBetweenUserAndAI, batchUpdateAssessments, AI_AGENT_ID } from '../../workspace/service/workspaceMessageService'
import { buildResearchDataset, downloadCSV } from './buildResearchDataset'
import { buildPerStudentExport, downloadJSON } from './buildStudentExport'
import { runCounterfactual, downloadCounterfactualResults } from './runCounterFactual'

import { buildSessionDataset, downloadSessionCSV } from './buildSessionDataset'
import { buildQueryDataset, downloadQueryCSV } from './buildQueryDataset'
import { buildQueryTypeExport, downloadQueryTypeCSV } from './exportQueryType'

// ─── Conversation Thread ────────────────────────────────────────────────────────

function ConversationThread({ student, onBack, assessment, onRunCounterfactual, counterfactualRunning, counterfactualProgress }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ padding: '24px 24px 16px', borderBottom: '1px solid #e8e8eb', flexShrink: 0 }}>
        <button
          onClick={onBack}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#888', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', marginLeft: '-8px', marginBottom: '12px', borderRadius: '6px' }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
        >
          ← Back to conversations
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#f0f0f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#111', fontFamily: 'monospace' }}>{student.name}</div>
            <div style={{ fontSize: '12px', color: '#888' }}>{student.messages.length} message{student.messages.length !== 1 ? 's' : ''}</div>
          </div>
          <button
            onClick={onRunCounterfactual}
            disabled={counterfactualRunning}
            style={{
              padding: '6px 14px', borderRadius: '20px', border: '1px solid #d0d5dd',
              background: counterfactualRunning ? '#e8e8eb' : 'white', color: counterfactualRunning ? '#888' : '#333',
              fontSize: '12px', fontWeight: 600, cursor: counterfactualRunning ? 'default' : 'pointer',
              transition: 'all 0.15s', whiteSpace: 'nowrap', flexShrink: 0,
            }}
          >
            {counterfactualRunning
              ? `⚡ ${counterfactualProgress?.done || 0}/${counterfactualProgress?.total || '?'}`
              : `⚡ Counterfactual (${student.messages.filter(m => m.role === 'user').length})`}
          </button>
        </div>

        {/* Counterfactual progress */}
        {counterfactualRunning && counterfactualProgress && (
          <div style={{ marginTop: '10px' }}>
            <div style={{ height: '3px', background: '#e8e8eb', borderRadius: '2px', overflow: 'hidden' }}>
              <div style={{ height: '100%', background: '#f59e0b', borderRadius: '2px', width: `${counterfactualProgress.total > 0 ? (counterfactualProgress.done / counterfactualProgress.total) * 100 : 0}%`, transition: 'width 0.3s ease' }} />
            </div>
            <div style={{ fontSize: '10px', color: '#888', marginTop: '4px' }}>{counterfactualProgress.message}</div>
          </div>
        )}

        {assessment && (
          <div style={{ marginTop: '12px', padding: '10px 12px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e8e8eb', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '11px', color: '#666' }}>
              <span style={{ fontWeight: 600, color: '#333' }}>Engagement:</span>{' '}
              {assessment.summary?.student?.engagement?.average ?? assessment.summary?.student?.quality?.average ?? '—'}/5
            </div>
            <div style={{ fontSize: '11px', color: '#666' }}>
              <span style={{ fontWeight: 600, color: '#333' }}>Query Types:</span>{' '}
              {Object.entries(assessment.summary?.student?.queryTypeDistribution || {}).filter(([, v]) => v > 0).map(([k, v]) => `${k}: ${v}`).join(', ') || '—'}
            </div>
            <div style={{ fontSize: '11px', color: '#666' }}>
              <span style={{ fontWeight: 600, color: '#333' }}>AI Alignment:</span>{' '}
              {Object.entries(assessment.summary?.ai?.pedagogicalAlignment || {}).filter(([, v]) => v > 0).map(([k, v]) => `${k}: ${v}`).join(', ') || '—'}
            </div>
          </div>
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {student.messages.map((msg, i) => {
          const studentAssessment = assessment?.studentAssessments?.find(a => a.messageIndex === i)
          const aiAssessment = assessment?.aiAssessments?.find(a => a.messageIndex === i)
          const msgAssessment = studentAssessment || aiAssessment

          return (
            <div key={i}>
              <div style={{ display: 'flex', gap: '10px', flexDirection: msg.role === 'user' ? 'row-reverse' : 'row' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: '#f0f0f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {msg.role === 'user' ? (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                  ) : (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="10" rx="2" /><circle cx="12" cy="5" r="2" /><path d="M12 7v4" /><line x1="8" y1="16" x2="8" y2="16" /><line x1="16" y1="16" x2="16" y2="16" /></svg>
                  )}
                </div>
                <div style={{ maxWidth: '75%', padding: '10px 14px', borderRadius: '10px', fontSize: '13px', lineHeight: '1.5', wordBreak: 'break-word', backgroundColor: msg.role === 'user' ? '#0070CC' : '#f5f5f7', color: msg.role === 'user' ? 'white' : '#333', border: msg.role === 'user' ? 'none' : '1px solid #e8e8eb' }}>
                  {msg.content}
                  <div style={{ fontSize: '10px', marginTop: '4px', opacity: 0.6, textAlign: msg.role === 'user' ? 'right' : 'left' }}>
                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' })}
                  </div>
                </div>
              </div>

              {msgAssessment && (
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', paddingLeft: msg.role === 'user' ? 0 : '38px', paddingRight: msg.role === 'user' ? '38px' : 0 }}>
                  {studentAssessment && (
                    <>
                      <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '10px', background: '#e8f0fe', color: '#1a73e8' }}>{studentAssessment.queryType}</span>
                      <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '10px', background: studentAssessment.queryQuality?.score >= 4 ? '#e6f4ea' : studentAssessment.queryQuality?.score >= 3 ? '#fef7e0' : '#fce8e6', color: studentAssessment.queryQuality?.score >= 4 ? '#137333' : studentAssessment.queryQuality?.score >= 3 ? '#b06000' : '#c5221f' }}>
                        Engagement: {studentAssessment.queryQuality?.score}/5
                      </span>
                    </>
                  )}
                  {aiAssessment && (
                    <>
                      <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '10px', background: '#f3e8fd', color: '#7627bb' }}>{aiAssessment.responseType}</span>
                      <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '10px', background: aiAssessment.pedagogicalAlignment?.level === 'Scaffolding' ? '#e6f4ea' : aiAssessment.pedagogicalAlignment?.level === 'Balanced' ? '#fef7e0' : '#fce8e6', color: aiAssessment.pedagogicalAlignment?.level === 'Scaffolding' ? '#137333' : aiAssessment.pedagogicalAlignment?.level === 'Balanced' ? '#b06000' : '#c5221f' }}>
                        {aiAssessment.pedagogicalAlignment?.level}
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Conversation Row ───────────────────────────────────────────────────────────

function ConversationRow({ student, onClick, hasAssessment }) {
  const lastUserMessage = student.messages.find(m => m.type === 'CHAT_QUERY')
  const preview = lastUserMessage ? lastUserMessage.content : ''

  return (
    <div
      onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', border: '1px solid #e8e8eb', borderRadius: '10px', cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s', background: 'white' }}
      onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f6ff'; e.currentTarget.style.borderColor = '#a8c8e8' }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#e8e8eb' }}
    >
      <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#f0f0f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#111', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{student.name}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            {hasAssessment && <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#34a853', display: 'inline-block' }} />}
            <span style={{ fontSize: '12px', color: '#888' }}>{student.messageCount} msg{student.messageCount !== 1 ? 's' : ''}</span>
          </div>
        </div>
        <div style={{ fontSize: '12px', color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: '2px' }}>{preview}</div>
      </div>
    </div>
  )
}

// ─── Modal ──────────────────────────────────────────────────────────────────────

export default function ConversationsModal({ open, onClose, students = [] }) {
  const { eventsByUser } = useContext(TelemetryDataContext)
  const { sessionData, sessionId } = useContext(SessionDataContext)
  const { analyticsById } = useContext(StudentAnalyticsContext)
  const [search, setSearch] = useState('')
  const [messageFilter, setMessageFilter] = useState('all')
  const [selectedStudent, setSelectedStudent] = useState(null)

  const [assessments, setAssessments] = useState({})
  const [assessmentRunning, setAssessmentRunning] = useState(false)
  const [assessmentProgress, setAssessmentProgress] = useState({ done: 0, total: 0, current: '' })
  const [latestResult, setLatestResult] = useState(null)
  const [downloading, setDownloading] = useState(false)
  const [downloadingJSON, setDownloadingJSON] = useState(false)
  const [loadingAssessments, setLoadingAssessments] = useState(false)
  const [showRerunConfirm, setShowRerunConfirm] = useState(false)
  const [counterfactualRunning, setCounterfactualRunning] = useState(false)
  const [counterfactualProgress, setCounterfactualProgress] = useState(null)

  const [downloadingSession, setDownloadingSession] = useState(false)
  const [downloadingQuery, setDownloadingQuery] = useState(false)

  // ─── Download Research Dataset ─────────────────────────────

  const handleDownload = useCallback(async () => {
    if (!sessionId) return
    setDownloading(true)
    try {
      const rows = await buildResearchDataset(students, eventsByUser, sessionId)
      downloadCSV(rows, `session_${sessionId}_query_dataset.csv`)
    } catch (err) {
      console.error('Export failed:', err)
    }
    setDownloading(false)
  }, [students, eventsByUser, sessionId])

  // ─── Build conversation data from telemetry events ─────────

  const studentsWithConvos = useMemo(() => {
    return students
      .map(student => {
        const events = eventsByUser[student.id] || eventsByUser[String(student.id)] || []
        const chatEvents = events.filter(e => e.type === 'CHAT_QUERY' || e.type === 'CHAT_RESPONSE' || e.type === 'CHAT_SEND')
        if (chatEvents.length === 0) return null

        const testResultEvents = events.filter(e => e.type === 'TEST_CASE_RESULT')
        const getTestResultBefore = (timestamp) => {
          const prior = testResultEvents.filter(e => e.timestamp <= timestamp)
          if (prior.length === 0) return null
          const last = prior[prior.length - 1]
          return { passed_count: last.payload?.passed_count || 0, total_tests: last.payload?.total_tests || 0 }
        }

        const getRecentError = (timestamp) => {
          const errors = events.filter(e => e.type === 'TERMINAL_ERROR' && e.timestamp <= timestamp)
          if (errors.length === 0) return null
          const last = errors[errors.length - 1]
          if (timestamp - last.timestamp > 30000) return null
          return last.payload?.message || null
        }

        const messages = chatEvents
          .filter(e => e.type === 'CHAT_QUERY' || e.type === 'CHAT_RESPONSE')
          .map(e => {
            const msg = {
              role: e.type === 'CHAT_QUERY' ? 'user' : 'assistant',
              type: e.type,
              content: e.payload?.text || e.payload?.message || e.payload?.content || '',
              timestamp: e.timestamp,
            }
            if (e.type === 'CHAT_QUERY') {
              // Code at query time = most recent CODE_* event payload.code before this query.
              // CHAT_QUERY events don't carry the editor state; CODE_* events do.
              const lastCodeBefore = [...events]
                .filter(ev => ev.type?.startsWith('CODE_') && ev.timestamp < e.timestamp && ev.payload?.code != null)
                .pop()
              msg.codeSnapshot = lastCodeBefore?.payload?.code || null
              msg.testResults = getTestResultBefore(e.timestamp)
              msg.errorOutput = getRecentError(e.timestamp)

              // Pre-query behavioral metrics
              const prevResponses = events.filter(ev => ev.type === 'CHAT_RESPONSE' && ev.timestamp < e.timestamp)
              const windowStart = prevResponses.length > 0
                ? prevResponses[prevResponses.length - 1].timestamp
                : events[0].timestamp
              const windowEvents = events.filter(ev => ev.timestamp >= windowStart && ev.timestamp < e.timestamp)

              const edits = windowEvents.filter(ev => ev.type === 'CODE_TYPE' || ev.type === 'CODE_INDENT').length
              const deletes = windowEvents.filter(ev => ev.type === 'CODE_DELETE' || ev.type === 'CODE_DELETE_SELECTION').length
              const runs = windowEvents.filter(ev => ev.type === 'TERMINAL_RUN').length
              const errors = windowEvents.filter(ev => ev.type === 'TERMINAL_ERROR').length
              const dur = Math.round((e.timestamp - windowStart) / 1000)

              let longestIdle = 0
              for (let j = 1; j < windowEvents.length; j++) {
                longestIdle = Math.max(longestIdle, (windowEvents[j].timestamp - windowEvents[j-1].timestamp) / 1000)
              }

              const prevQueries = events.filter(ev => ev.type === 'CHAT_QUERY' && ev.timestamp < e.timestamp)
              const lastQuery = prevQueries.length > 0 ? prevQueries[prevQueries.length - 1] : null
              const timeSinceLastQuery = lastQuery ? Math.round((e.timestamp - lastQuery.timestamp) / 1000) : null

              let pattern = 'read_only'
              if (edits > 0 && runs === 0) pattern = 'wrote_no_test'
              else if (runs > 0) pattern = 'debug_test'
              else if (edits === 0 && runs === 0 && timeSinceLastQuery && timeSinceLastQuery < 15) pattern = 'rapid_reply'

              msg.codeEdits = edits
              msg.codeDeletes = deletes
              msg.terminalRuns = runs
              msg.terminalErrors = errors
              msg.timeSinceLastQuery = timeSinceLastQuery
              msg.longestIdle = Math.round(longestIdle)
              msg.windowDuration = dur
              msg.behavioralPattern = pattern
            }
            return msg
          }).sort((a, b) => a.timestamp - b.timestamp)

        const queryCount = chatEvents.filter(e => e.type === 'CHAT_QUERY').length
        const name = student.display_name || student.username || `${student.first_name || ''} ${student.last_name || ''}`.trim() || 'Unknown'
        return { id: student.id, name, messages, messageCount: queryCount }
      })
      .filter(Boolean)
      .sort((a, b) => b.messageCount - a.messageCount)
  }, [students, eventsByUser])

  // ─── Load existing assessments from DB on open ─────────────

  useEffect(() => {
    if (!open || !sessionId || studentsWithConvos.length === 0) return
    if (Object.keys(assessments).length > 0) return

    let cancelled = false

    const loadExisting = async () => {
      setLoadingAssessments(true)
      const existing = {}

      for (const student of studentsWithConvos) {
        try {
          const dbMessages = await getMessagesBetweenUserAndAI(student.id, sessionId)
          const hasAssessment = dbMessages.some(m => m.query_type)
          if (!hasAssessment) continue

          const studentAssessments = dbMessages
            .filter(m => m.query_type)
            .map((m, i) => ({
              messageIndex: i, content: m.content, queryType: m.query_type,
              queryQuality: { score: m.query_quality_score, ...(m.assessment?.queryQuality || {}) },
              confidence: m.assessment?.confidence || null,
            }))

          const aiAssessments = dbMessages
            .filter(m => m.response_type)
            .map((m, i) => ({
              messageIndex: i, content: m.content, responseType: m.response_type,
              pedagogicalAlignment: { level: m.pedagogical_alignment, ...(m.assessment?.pedagogicalAlignment || {}) },
              containsCode: m.assessment?.containsCode || false,
              codeLineCount: m.assessment?.codeLineCount || 0,
              confidence: m.assessment?.confidence || null,
            }))

          const queryTypes = {}
          const qualityScores = []

          studentAssessments.forEach(sa => {
            queryTypes[sa.queryType] = (queryTypes[sa.queryType] || 0) + 1
            if (sa.queryQuality?.score) qualityScores.push(sa.queryQuality.score)
          })

          const alignDist = { Scaffolding: 0, Balanced: 0, Enabling: 0 }
          const responseTypes = {}
          aiAssessments.forEach(aa => {
            responseTypes[aa.responseType] = (responseTypes[aa.responseType] || 0) + 1
            if (alignDist[aa.pedagogicalAlignment?.level] !== undefined) alignDist[aa.pedagogicalAlignment.level]++
          })

          const avgQ = qualityScores.length > 0 ? qualityScores.reduce((a, b) => a + b, 0) / qualityScores.length : null

          existing[student.id] = {
            studentAssessments, aiAssessments,
            summary: {
              student: {
                totalQueries: studentAssessments.length, queryTypeDistribution: queryTypes,
                engagement: { average: avgQ ? Math.round(avgQ * 100) / 100 : null },
              },
              ai: {
                responseTypeDistribution: responseTypes, pedagogicalAlignment: alignDist,
                codeProvisionRate: aiAssessments.length > 0 ? Math.round((aiAssessments.filter(a => a.containsCode).length / aiAssessments.length) * 100 * 10) / 10 : 0,
              },
              exchangePatterns: {},
            },
          }
        } catch (err) {
          console.error(`Failed to load assessments for student ${student.id}:`, err)
        }
      }

      if (!cancelled && Object.keys(existing).length > 0) {
        setAssessments(existing)
        console.log(`Loaded ${Object.keys(existing).length} existing assessments from DB`)
      }
      if (!cancelled) setLoadingAssessments(false)
    }

    loadExisting()
    return () => { cancelled = true }
  }, [open, sessionId, studentsWithConvos])

  // ─── Batch Assessment (supports forceRerun) ────────────────

  const runBatchAssessment = useCallback(async ({ forceRerun = false } = {}) => {
    const taskDescription = sessionData?.taskDescription
    if (!taskDescription) {
      console.error('No task description available')
      return
    }

    if (forceRerun) setAssessments({})

    const currentAssessments = forceRerun ? {} : assessments
    const toAssess = forceRerun
      ? studentsWithConvos
      : studentsWithConvos.filter(s => !currentAssessments[s.id])

    if (toAssess.length === 0) return

    setAssessmentRunning(true)
    setAssessmentProgress({ done: 0, total: toAssess.length, current: '' })

    const BATCH_SIZE = 5
    const results = { ...currentAssessments }

    for (let i = 0; i < toAssess.length; i += BATCH_SIZE) {
      const batch = toAssess.slice(i, i + BATCH_SIZE)
      setAssessmentProgress(prev => ({ ...prev, current: batch.map(s => s.name).join(', ') }))

      const batchResults = await Promise.all(
        batch.map(async (student) => {
          try {
            const result = await batchAssessConversation(taskDescription, student.messages)

            if (result && sessionId) {
              try {
                const dbMessages = await getMessagesBetweenUserAndAI(student.id, sessionId)
                const updates = []

                ;(result.studentAssessments || []).forEach(sa => {
                  const match = dbMessages.find(m => m.sender_id === student.id && m.content === sa.content)
                  if (match) {
                    updates.push({
                      messageId: match.id,
                      assessmentData: {
                        query_type: sa.queryType,
                        query_quality_score: sa.queryQuality?.score,
                        assessment: { queryQuality: sa.queryQuality, confidence: sa.confidence }
                      }
                    })
                  }
                })

                ;(result.aiAssessments || []).forEach(aa => {
                  const match = dbMessages.find(m => m.sender_id === AI_AGENT_ID && m.content === aa.content)
                  if (match) {
                    updates.push({
                      messageId: match.id,
                      assessmentData: {
                        response_type: aa.responseType,
                        pedagogical_alignment: aa.pedagogicalAlignment?.level,
                        assessment: { pedagogicalAlignment: aa.pedagogicalAlignment, containsCode: aa.containsCode, codeLineCount: aa.codeLineCount, confidence: aa.confidence }
                      }
                    })
                  }
                })

                if (updates.length > 0) {
                  await batchUpdateAssessments(updates)
                  console.log(`Saved ${updates.length} assessments for ${student.name}`)
                }
              } catch (saveErr) {
                console.error(`Failed to save assessments for ${student.name}:`, saveErr)
              }
            }
            return { id: student.id, result }
          } catch (err) {
            console.error(`Assessment failed for ${student.name}:`, err)
            return { id: student.id, result: null }
          }
        })
      )

      batchResults.forEach(({ id, result }) => {
        if (result) {
          results[id] = result
          const student = toAssess.find(s => s.id === id)
          setLatestResult({ name: student?.name || id, result })
        }
      })

      setAssessments({ ...results })
      setAssessmentProgress(prev => ({ ...prev, done: Math.min(i + BATCH_SIZE, toAssess.length) }))
    }

    setAssessmentRunning(false)
    console.log('Batch assessment complete:', results)
  }, [studentsWithConvos, assessments, sessionData, sessionId])

  // ─── Counterfactual Runner ─────────────────────────────────

  const handleRunCounterfactual = useCallback(async (testOne = false) => {
    if (!sessionId || !sessionData?.taskDescription) {
      console.error('Missing session ID or task description')
      return
    }
    setCounterfactualRunning(true)
    setCounterfactualProgress({ phase: 'loading', message: 'Starting...', done: 0, total: 0 })
    try {
      let targetStudents = studentsWithConvos.map(s => ({ id: s.id, display_name: s.name }))

      if (testOne) {
        const random = targetStudents[Math.floor(Math.random() * targetStudents.length)]
        targetStudents = [random]
        console.log('Testing counterfactual with:', random.display_name)
      }

      const results = await runCounterfactual(
        targetStudents,
        eventsByUser,
        sessionId,
        sessionData.taskDescription,
        setCounterfactualProgress,
      )
      downloadCounterfactualResults(results, sessionId)
    } catch (err) {
      console.error('Counterfactual run failed:', err)
    }
    setCounterfactualRunning(false)
    setCounterfactualProgress(null)
  }, [studentsWithConvos, eventsByUser, sessionId, sessionData])

  const handleRunCounterfactualForStudent = useCallback(async (studentId, studentName) => {
    if (!sessionId || !sessionData?.taskDescription) {
      console.error('Missing session ID or task description')
      return
    }
    setCounterfactualRunning(true)
    setCounterfactualProgress({ phase: 'loading', message: `Running ${studentName}...`, done: 0, total: 0 })
    try {
      const results = await runCounterfactual(
        [{ id: studentId, display_name: studentName }],
        eventsByUser,
        sessionId,
        sessionData.taskDescription,
        setCounterfactualProgress,
      )
      downloadCounterfactualResults(results, sessionId)
    } catch (err) {
      console.error('Counterfactual failed:', err)
    }
    setCounterfactualRunning(false)
    setCounterfactualProgress(null)
  }, [eventsByUser, sessionId, sessionData])

  // ─── Class-wide summary ────────────────────────────────────

  const classSummary = useMemo(() => {
    const assessed = Object.values(assessments).filter(Boolean)
    if (assessed.length === 0) return null

    const allQueryTypes = {}
    const engagementScores = []
    const allResponseTypes = {}
    const allAlignment = { Scaffolding: 0, Balanced: 0, Enabling: 0 }

    assessed.forEach(a => {
      const s = a.summary?.student
      const ai = a.summary?.ai
      if (s) {
        Object.entries(s.queryTypeDistribution || {}).forEach(([k, v]) => { allQueryTypes[k] = (allQueryTypes[k] || 0) + v })
        const avg = s.engagement?.average ?? s.quality?.average
        if (avg) engagementScores.push(avg)
      }
      if (ai) {
        Object.entries(ai.responseTypeDistribution || {}).forEach(([k, v]) => { allResponseTypes[k] = (allResponseTypes[k] || 0) + v })
        Object.entries(ai.pedagogicalAlignment || {}).forEach(([k, v]) => { if (allAlignment[k] !== undefined) allAlignment[k] += v })
      }
    })

    const avgEng = engagementScores.length > 0
      ? (engagementScores.reduce((a, b) => a + b, 0) / engagementScores.length).toFixed(2)
      : null

    return {
      studentsAssessed: assessed.length,
      queryTypeDistribution: allQueryTypes,
      averageEngagement: avgEng,
      responseTypeDistribution: allResponseTypes,
      pedagogicalAlignment: allAlignment,
    }
  }, [assessments])

  // ─── Filter ────────────────────────────────────────────────

  const filtered = useMemo(() => {
    return studentsWithConvos.filter(s => {
      const q = search.toLowerCase()
      const matchesSearch = !q || s.name.toLowerCase().includes(q) || s.messages.some(m => m.content.toLowerCase().includes(q))
      let matchesMessages = true
      if (messageFilter === '1-3') matchesMessages = s.messageCount >= 1 && s.messageCount <= 3
      else if (messageFilter === '4-7') matchesMessages = s.messageCount >= 4 && s.messageCount <= 7
      else if (messageFilter === '8-12') matchesMessages = s.messageCount >= 8 && s.messageCount <= 12
      else if (messageFilter === '13+') matchesMessages = s.messageCount >= 13
      return matchesSearch && matchesMessages
    })
  }, [studentsWithConvos, search, messageFilter])

  if (!open) return null

  const handleClose = () => { setSelectedStudent(null); setSearch(''); setMessageFilter('all'); setShowRerunConfirm(false); onClose() }

  const assessedCount = Object.keys(assessments).length
  const totalWithConvos = studentsWithConvos.length

  const selectStyle = {
    padding: '6px 28px 6px 12px', borderRadius: '20px', border: '1px solid #e0e0e0', background: '#f7f7f8', fontSize: '12px', color: '#444', cursor: 'pointer', outline: 'none', appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%23666' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center',
  }

  const cfBtnStyle = (disabled) => ({
    padding: '6px 14px', borderRadius: '20px', border: '1px solid #d0d5dd',
    background: disabled ? '#e8e8eb' : 'white', color: disabled ? '#888' : '#333',
    fontSize: '12px', fontWeight: 600, cursor: disabled ? 'default' : 'pointer',
    transition: 'all 0.15s', whiteSpace: 'nowrap',
  })

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: '12px', maxWidth: '640px', width: '90%', height: '85vh', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {selectedStudent ? (
          <ConversationThread
            student={selectedStudent}
            onBack={() => setSelectedStudent(null)}
            assessment={assessments[selectedStudent.id] || null}
            counterfactualRunning={counterfactualRunning}
            counterfactualProgress={counterfactualProgress}
            onRunCounterfactual={() => handleRunCounterfactualForStudent(selectedStudent.id, selectedStudent.name)}
          />
        ) : (
          <>
            {/* Header */}
            <div style={{ padding: '24px 24px 16px', borderBottom: '1px solid #e8e8eb', flexShrink: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#111' }}>Conversations</h2>
                  <span style={{ fontSize: '12px', color: '#888', marginTop: '2px', display: 'block' }}>{filtered.length} student{filtered.length !== 1 ? 's' : ''} with LLM conversations</span>
                </div>
                <button onClick={handleClose} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#666', padding: '4px 8px', lineHeight: 1 }}>✕</button>
              </div>

              {/* Search */}
              <div
                style={{ display: 'flex', alignItems: 'center', border: '2px solid #d0d5dd', borderRadius: '10px', padding: '8px 12px', marginBottom: '12px', transition: 'border-color 0.2s' }}
                onFocus={(e) => e.currentTarget.style.borderColor = '#5ba0d6'}
                onBlur={(e) => e.currentTarget.style.borderColor = '#d0d5dd'}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px', flexShrink: 0 }}><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                <input type="text" placeholder="Search conversations..." value={search} onChange={(e) => setSearch(e.target.value)} style={{ border: 'none', outline: 'none', fontSize: '13px', width: '100%', color: '#333', background: 'transparent' }} />
                {search && <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', padding: '2px' }}>✕</button>}
              </div>

              {/* Filters + Assess + Rerun + Export */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <select value={messageFilter} onChange={(e) => setMessageFilter(e.target.value)} style={selectStyle}>
                    <option value="all">All messages</option>
                    <option value="1-3">1 - 3 msgs</option>
                    <option value="4-7">4 - 7 msgs</option>
                    <option value="8-12">8 - 12 msgs</option>
                    <option value="13+">13+ msgs</option>
                  </select>
                  {messageFilter !== 'all' && (
                    <button onClick={() => setMessageFilter('all')} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#888', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'} onMouseLeave={(e) => e.currentTarget.style.background = 'none'}>
                      ✕ Clear
                    </button>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {/* Assess / Continue */}
                  <button
                    onClick={() => runBatchAssessment()}
                    disabled={assessmentRunning || assessedCount === totalWithConvos || loadingAssessments}
                    style={{
                      padding: '6px 14px', borderRadius: '20px', border: 'none', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap', transition: 'all 0.15s',
                      background: loadingAssessments || assessmentRunning ? '#e8e8eb' : assessedCount === totalWithConvos ? '#e6f4ea' : '#111',
                      color: loadingAssessments || assessmentRunning ? '#888' : assessedCount === totalWithConvos ? '#137333' : 'white',
                      cursor: assessmentRunning || assessedCount === totalWithConvos || loadingAssessments ? 'default' : 'pointer',
                    }}
                  >
                    {loadingAssessments ? 'Loading from DB...'
                      : assessmentRunning ? `Assessing... ${assessmentProgress.done}/${assessmentProgress.total}`
                      : assessedCount === totalWithConvos ? `✓ ${assessedCount} assessed`
                      : assessedCount > 0 ? `Continue (${assessedCount}/${totalWithConvos})`
                      : 'Assess All'}
                  </button>

                  {/* Rerun All */}
                  {assessedCount > 0 && !assessmentRunning && !loadingAssessments && (
                    <button
                      onClick={() => setShowRerunConfirm(true)}
                      style={{ padding: '6px 14px', borderRadius: '20px', border: '1px solid #d0d5dd', background: 'white', color: '#333', fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#fff1f0'; e.currentTarget.style.borderColor = '#f5a89a' }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#d0d5dd' }}
                    >
                      ↻ Rerun All
                    </button>
                  )}

                  {/* Export CSV */}
                  <button onClick={handleDownload} disabled={downloading} style={cfBtnStyle(downloading)}>
                    {downloading ? 'Exporting...' : '↓ CSV'}
                  </button>

                  {/* Export JSON */}
                  <button onClick={async () => {
                    setDownloadingJSON(true)
                    try {
                      const data = buildPerStudentExport(analyticsById, students, sessionId)
                      downloadJSON(data, `session_${sessionId}_student_export.json`)
                    } catch (err) { console.error('JSON export failed:', err) }
                    setDownloadingJSON(false)
                  }} disabled={downloadingJSON} style={cfBtnStyle(downloadingJSON)}>
                    {downloadingJSON ? 'Exporting...' : '↓ JSON'}
                  </button>

                  {/* Session-Level CSV */}
                  <button onClick={async () => {
                    setDownloadingSession(true)
                    try {
                      const rows = await buildSessionDataset(students, eventsByUser, sessionId, analyticsById)
                      downloadSessionCSV(rows, `session_${sessionId}_session_level.csv`)
                    } catch (err) { console.error('Session export failed:', err) }
                    setDownloadingSession(false)
                  }} disabled={downloadingSession} style={cfBtnStyle(downloadingSession)}>
                    {downloadingSession ? 'Exporting...' : '↓ Session'}
                  </button>

                  {/* Query-Level CSV */}
                  <button onClick={async () => {
                    setDownloadingQuery(true)
                    try {
                      const rows = await buildQueryDataset(students, eventsByUser, sessionId, analyticsById)
                      downloadQueryCSV(rows, `session_${sessionId}_query_level.csv`)
                    } catch (err) { console.error('Query export failed:', err) }
                    setDownloadingQuery(false)
                  }} disabled={downloadingQuery} style={cfBtnStyle(downloadingQuery)}>
                    {downloadingQuery ? 'Exporting...' : '↓ Query'}
                  </button>

                  <button onClick={async () => {
                    try {
                      const rows = await buildQueryTypeExport(students, eventsByUser, sessionId)
                      downloadQueryTypeCSV(rows, `deployment_${sessionId}_labels.csv`)
                    } catch (err) { console.error('Label export failed:', err) }
                  }} style={cfBtnStyle(false)}>
                    ↓ Labels
                  </button>

                  {/* Counterfactual: Test 1 */}
                  <button onClick={() => handleRunCounterfactual(true)} disabled={counterfactualRunning} style={cfBtnStyle(counterfactualRunning)}>
                    {counterfactualRunning ? `${counterfactualProgress?.done || 0}/${counterfactualProgress?.total || '?'}` : '⚡ Test 1'}
                  </button>

                  {/* Counterfactual: Run All */}
                  <button onClick={() => handleRunCounterfactual(false)} disabled={counterfactualRunning} style={cfBtnStyle(counterfactualRunning)}>
                    {counterfactualRunning ? `${counterfactualProgress?.done || 0}/${counterfactualProgress?.total || '?'}` : '⚡ Run All'}
                  </button>
                </div>
              </div>

              {/* Counterfactual Progress */}
              {counterfactualRunning && counterfactualProgress && (
                <div style={{ marginTop: '10px' }}>
                  <div style={{ height: '3px', background: '#e8e8eb', borderRadius: '2px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', background: '#f59e0b', borderRadius: '2px', width: `${counterfactualProgress.total > 0 ? (counterfactualProgress.done / counterfactualProgress.total) * 100 : 0}%`, transition: 'width 0.3s ease' }} />
                  </div>
                  <div style={{ fontSize: '10px', color: '#888', marginTop: '4px' }}>{counterfactualProgress.message}</div>
                </div>
              )}

              {/* Assessment Progress */}
              {assessmentRunning && (
                <div style={{ marginTop: '10px' }}>
                  <div style={{ height: '3px', background: '#e8e8eb', borderRadius: '2px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', background: '#111', borderRadius: '2px', width: `${assessmentProgress.total > 0 ? (assessmentProgress.done / assessmentProgress.total) * 100 : 0}%`, transition: 'width 0.3s ease' }} />
                  </div>
                  <div style={{ fontSize: '10px', color: '#888', marginTop: '4px' }}>{assessmentProgress.current}</div>
                </div>
              )}

              {/* Class summary */}
              {classSummary && !assessmentRunning && (
                <div style={{ marginTop: '12px', padding: '12px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e8e8eb' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#333', marginBottom: '8px' }}>Class Summary ({classSummary.studentsAssessed} students)</div>
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ fontSize: '11px', color: '#666' }}><span style={{ fontWeight: 600 }}>Avg Engagement:</span> {classSummary.averageEngagement}/5</div>
                    <div style={{ fontSize: '11px', color: '#666' }}><span style={{ fontWeight: 600 }}>AI Alignment:</span> {Object.entries(classSummary.pedagogicalAlignment).filter(([, v]) => v > 0).map(([k, v]) => `${k}: ${v}`).join(', ')}</div>
                  </div>
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '6px' }}>
                    <span style={{ fontWeight: 600 }}>Query Types:</span>{' '}
                    {Object.entries(classSummary.queryTypeDistribution).sort(([, a], [, b]) => b - a).map(([k, v]) => `${k} (${v})`).join(', ')}
                  </div>
                </div>
              )}
            </div>

            {/* List */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {filtered.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '64px 0', textAlign: 'center' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#f0f0f2', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 500, color: '#111' }}>No conversations found</div>
                  <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>Try adjusting your search</div>
                </div>
              ) : (
                filtered.map(student => (
                  <ConversationRow key={student.id} student={student} onClick={() => setSelectedStudent(student)} hasAssessment={!!assessments[student.id]} />
                ))
              )}
            </div>

            {/* Footer */}
            {filtered.length > 0 && (
              <div style={{ padding: '12px 24px', borderTop: '1px solid #e8e8eb', backgroundColor: '#fafafa', textAlign: 'center' }}>
                <span style={{ fontSize: '12px', color: '#888' }}>{filtered.length} conversation{filtered.length !== 1 ? 's' : ''}{assessedCount > 0 && ` · ${assessedCount} assessed`}</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* ─── Rerun Confirmation ──────────────────────────────── */}
      {showRerunConfirm && (
        <div onClick={() => setShowRerunConfirm(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '12px', maxWidth: '400px', width: '90%', padding: '24px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: '#111', marginBottom: '8px' }}>Rerun all assessments?</div>
            <div style={{ fontSize: '13px', color: '#666', lineHeight: 1.6, marginBottom: '20px' }}>
              This will re-assess all {totalWithConvos} conversations using the current rubric and overwrite existing scores in the database. This cannot be undone.
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowRerunConfirm(false)} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #d0d5dd', background: 'white', color: '#333', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={() => { setShowRerunConfirm(false); runBatchAssessment({ forceRerun: true }) }} style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: '#111', color: 'white', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>Rerun All</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Result Popup ─────────────────────────────────────── */}
      {latestResult && (
        <div onClick={() => setLatestResult(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '12px', maxWidth: '560px', width: '90%', maxHeight: '70vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e8e8eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#111' }}>Assessment Result</div>
                <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>{latestResult.name}</div>
              </div>
              <button onClick={() => setLatestResult(null)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#666', padding: '4px 8px', lineHeight: 1 }}>✕</button>
            </div>

            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e8e8eb' }}>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <div style={{ fontSize: '12px', color: '#666' }}><span style={{ fontWeight: 600, color: '#333' }}>Queries:</span> {latestResult.result?.summary?.student?.totalQueries ?? 0}</div>
                <div style={{ fontSize: '12px', color: '#666' }}><span style={{ fontWeight: 600, color: '#333' }}>Avg Engagement:</span> {latestResult.result?.summary?.student?.engagement?.average ?? latestResult.result?.summary?.student?.quality?.average ?? '—'}/5</div>
                <div style={{ fontSize: '12px', color: '#666' }}><span style={{ fontWeight: 600, color: '#333' }}>AI Code Rate:</span> {latestResult.result?.summary?.ai?.codeProvisionRate ?? 0}%</div>
              </div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '8px' }}>
                {Object.entries(latestResult.result?.summary?.student?.queryTypeDistribution || {}).filter(([, v]) => v > 0).map(([k, v]) => (
                  <span key={k} style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '10px', background: '#e8f0fe', color: '#1a73e8' }}>{k}: {v}</span>
                ))}
                {Object.entries(latestResult.result?.summary?.ai?.pedagogicalAlignment || {}).filter(([, v]) => v > 0).map(([k, v]) => (
                  <span key={k} style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '10px', background: '#f3e8fd', color: '#7627bb' }}>{k}: {v}</span>
                ))}
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
              {(latestResult.result?.studentAssessments || []).map((sa, i) => (
                <div key={i} style={{ marginBottom: '12px', padding: '10px 12px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e8e8eb' }}>
                  <div style={{ fontSize: '12px', color: '#333', marginBottom: '6px', fontStyle: 'italic' }}>
                    "{sa.content?.length > 120 ? sa.content.slice(0, 120) + '...' : sa.content}"
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '10px', background: '#e8f0fe', color: '#1a73e8' }}>{sa.queryType}</span>
                    <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '10px', background: sa.queryQuality?.score >= 4 ? '#e6f4ea' : sa.queryQuality?.score >= 3 ? '#fef7e0' : '#fce8e6', color: sa.queryQuality?.score >= 4 ? '#137333' : sa.queryQuality?.score >= 3 ? '#b06000' : '#c5221f' }}>
                      Engagement: {sa.queryQuality?.score}/5
                    </span>
                  </div>
                  {sa.queryQuality?.rationale && (
                    <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>{sa.queryQuality.rationale}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}